import { ApolloClient, ApolloLink, defaultDataIdFromObject, HttpLink, InMemoryCache, Observable } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { setContext } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { relayStylePagination } from '@apollo/client/utilities'
import isEqual from 'lodash/isEqual'
import memoize from 'lodash/memoize'
import locales from '@/constants/locales'
import { analytics } from '@/services/Analytics'
import {
  AdditionalHeaders,
  BaseClientConstructor,
  FEDERATION_CLIENT_NAME,
  FEDERATION_CLIENT_VERSION,
} from '@/services/ApolloClient/utils'
import { getClientAccessToken } from '@/services/OAuthService'
import { getRID } from '@/utils/RIDUtil'
import { getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { isProductionEnvironment } from '@/utils/environment-utils'
import { getHydraBaseUrl } from '@/utils/environment-variables'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { Maybe } from '@/utils/types'
import { logErrorLink } from './error-link'
import generatedPossibleTypes from './generated-possibleTypes.json'

const webAuthLink = (accessToken?: string) =>
  setContext(async (_, { headers }) => {
    const token = accessToken ? accessToken : await getClientAccessToken()

    const additionalHeaders: AdditionalHeaders = {}
    additionalHeaders['X-RID'] = getRID()
    additionalHeaders['X-PLATFORM'] = FEDERATION_CLIENT_NAME

    if (token) {
      additionalHeaders.Authorization = token
    }

    return {
      headers: {
        ...headers,
        ...additionalHeaders,
      },
    }
  })

const requireAuthLink = new ApolloLink((operation, forward) => {
  const { headers = {} } = operation.getContext()
  const requireAuth = operation.getContext().requireAuth

  if (requireAuth && !headers.Authorization) {
    logger().debug('Skipping graph request because it required auth and auth is missing', {
      operationName: operation.operationName,
      variables: operation.variables,
    })
    return new Observable((subscriber) => {
      subscriber.next({ data: null })
      subscriber.complete()
    })
  }

  return forward(operation)
})

export const viewerProfileIdLink = (viewerProfileId: string) => {
  return setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        'X-VIEWER-PROFILE-ID': viewerProfileId,
      },
    }
  })
}

const deviceIdLink = setContext((_, { headers }) => {
  const {
    user: { anonymousId },
  } = analytics.getState()

  const sessionId = getAnalyticsSessionId()

  return {
    headers: {
      ...headers,
      'X-DID': anonymousId,
      'X-SEGMENT-ANON-ID': anonymousId,
      'X-PLATFORM': FEDERATION_CLIENT_NAME,
      'X-ANGEL-APP-NAME': FEDERATION_CLIENT_NAME,
      'X-ANGEL-APP-VERSION': FEDERATION_CLIENT_VERSION,
      ...(sessionId ? { 'X-SESSION-ID': sessionId } : {}),
    },
  }
})

export const hydraLocaleLink = (locale: Maybe<string>, region?: Maybe<string>) => {
  return setContext((_, { headers }) => {
    const newHeaders = { ...headers }
    if (locale) {
      newHeaders['X-Accept-Language'] = locale
    }
    if (region) {
      // x-region may do nothing. it may be old. Leaving in for now just in case.
      newHeaders['X-Region'] = region
      newHeaders['X-Region-Override'] = region
    }

    return { headers: newHeaders }
  })
}

const CONTENTFUL_LOCALE_MAP = {
  es: 'es-419',
  fr: 'fr',
  'zh-TW': 'zh-Hant',
} as Record<string, string>

const contentfulLocaleLink = (locale: Maybe<string>) => {
  const finalLocale = locale && locale in CONTENTFUL_LOCALE_MAP ? CONTENTFUL_LOCALE_MAP[locale] : locale
  return new ApolloLink((operation, forward) => {
    operation.variables = { ...operation.variables, locale: finalLocale }
    return forward(operation)
  })
}

const contentfulLocaleFallback = new ErrorLink(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    // Retry the request with a different locale if the original request fails

    const originalVariables = operation.variables
    const originalLocale = originalVariables.locale

    const isNotUsableLocale = !locales.all.includes(originalLocale) || !originalLocale?.startsWith('en')
    const newLocale = isNotUsableLocale ? 'en' : originalLocale
    operation.variables = { ...originalVariables, locale: newLocale }

    return forward(operation)
  }
})

const getMemoizedWebClient = memoize(
  (opts: BaseClientConstructor) => constructWebClient(opts),
  ({ baseUrl, version, locale, name }) => `${baseUrl}_${locale}_${version}_${name}`,
)

const constructWebClient = ({
  accessToken,
  clientName,
  baseUrl,
  version,
  name,
  locale,
  viewerProfileId,
}: BaseClientConstructor) => {
  const backend = new HttpLink({ uri: `${baseUrl}/graphql` })

  return new ApolloClient({
    // set to true for SSR
    ssrMode: typeof window === 'undefined',

    link: ApolloLink.from(
      [
        logErrorLink(clientName),
        new RetryLink(),
        webAuthLink(accessToken),
        requireAuthLink,
        viewerProfileId ? viewerProfileIdLink(viewerProfileId) : undefined,
        hydraLocaleLink(locale),
        deviceIdLink,
        backend,
      ].filter(isDefined),
    ),

    cache: new InMemoryCache({
      possibleTypes: generatedPossibleTypes,
      typePolicies: {
        Query: {
          // Merge incoming query results with existing cached data instead of replacing.
          // This prevents "Missing field" warnings when different queries write partial
          // results to the cache (e.g., one query fetches 'viewer', another fetches 'project').
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming)
          },
          fields: {
            reservations: relayStylePagination(['input']),
          },
        },
        User: {
          keyFields: (object) => {
            if (!object.id) {
              logger().error('Missing client cache key for graphql entity', { object })
              throw new Error('Missing client cache key for graphql entity')
            }
            return `User:${object.id}`
          },
        },
        Content: {
          keyFields: (object) => {
            if (!object.id) {
              logger().error('Missing client cache key for graphql entity', { object })
            }
            return `Content:${object.id}`
          },
        },
        Project: {
          fields: {
            metadata: {
              merge: mergeContent,
            },
          },
        },
        Metadata: {
          merge: true,
        },
        ComingSoonTagGroup: {
          fields: {
            connection: relayStylePagination(['preview']),
          },
        },
        ContentEpisode: {
          fields: {
            image: {
              merge: mergeContent,
            },
          },
        },
        ContentMovie: {
          fields: {
            image: {
              merge: mergeContent,
            },
          },
        },
        ContentSpecial: {
          fields: {
            image: {
              merge: mergeContent,
            },
          },
        },
        ContentSeries: {
          fields: {
            image: {
              merge: mergeContent,
            },
          },
        },
        Episode: {
          fields: {
            source: {
              merge: mergeContent,
            },
          },
        },
      },
    }),

    clientAwareness: {
      name: name ?? FEDERATION_CLIENT_NAME,
      version: version ?? FEDERATION_CLIENT_VERSION,
    },
  })
}

const createContentfulClient = memoize(
  ({ baseUrl, locale, clientName }: BaseClientConstructor) => {
    const backend = new HttpLink({ uri: baseUrl })
    return new ApolloClient({
      ssrMode: typeof window === 'undefined',

      link: ApolloLink.from([
        logErrorLink(clientName),
        new RetryLink({
          delay: {
            initial: 300,
            max: 3000,
            jitter: true,
          },
          attempts: {
            max: 3,
            retryIf: (error) => {
              // Retry on network errors but not on GraphQL errors (which indicate valid responses)
              return !!error && !CombinedGraphQLErrors.is(error)
            },
          },
        }),
        contentfulLocaleLink(locale),
        contentfulLocaleFallback,
        backend,
      ]),

      cache: new InMemoryCache({
        dataIdFromObject(responseObject) {
          const resp = responseObject as { sys?: { id?: string } }
          if (resp?.sys?.id) {
            return resp.sys.id as string
          }
          return defaultDataIdFromObject(responseObject)
        },
        typePolicies: {
          Query: {
            // Merge incoming query results with existing cached data instead of replacing.
            // This prevents "Missing field" warnings when different queries write partial
            // results to the cache (e.g., one query fetches 'saleCollection', another fetches 'postCollection').
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      }),
    })
  },
  ({ baseUrl, name, version, locale }: BaseClientConstructor) => `${baseUrl}_${name}_${version}_${locale}`,
)

export const getShopifyClient = memoize(() => {
  return new ApolloClient({
    link: ApolloLink.from([
      logErrorLink('shopify-v2'),
      new RetryLink(),
      new HttpLink({
        uri: `https://${process.env.NEXT_PUBLIC_SHOPIFY_DOMAIN}/api/2024-10/graphql.json`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-SDK-Variant': 'storefront-api-client',
          'X-SDK-Version': '1.0.2',
          'X-Shopify-Storefront-Access-Token': process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN as string,
        },
      }),
    ]),

    cache: new InMemoryCache(),
  })
})

export interface WebClientConfig {
  accessToken?: string
  locale?: string
  baseUrl?: string
  viewerProfileId?: string
}

export const getWebClient = (config?: WebClientConfig) => {
  return getMemoizedWebClient({
    clientName: 'memoized-client-side-federation',
    baseUrl: config?.baseUrl ?? getHydraBaseUrl(),
    locale: config?.locale,
    viewerProfileId: config?.viewerProfileId,
    name: FEDERATION_CLIENT_NAME,
    version: FEDERATION_CLIENT_VERSION,
  })
}

export const createWebClient = (config?: WebClientConfig) => {
  return constructWebClient({
    accessToken: config?.accessToken,
    clientName: 'client-side-federation',
    baseUrl: config?.baseUrl ?? getHydraBaseUrl(),
    locale: config?.locale,
    name: FEDERATION_CLIENT_NAME,
    version: FEDERATION_CLIENT_VERSION,
    viewerProfileId: config?.viewerProfileId,
  })
}

export interface ContentfulClientOptions {
  locale?: string
  preview?: boolean
}

export const getSharedContentfulClient = (options?: ContentfulClientOptions): ApolloClient => {
  const { locale } = options ?? {}

  return createContentfulClient({
    clientName: 'shared-contentful',
    baseUrl: `https://graphql.contentful.com/content/v1/spaces/${process.env.NEXT_PUBLIC_SHARED_CONTENTFUL_SPACE_ID}?access_token=${process.env.NEXT_PUBLIC_SHARED_CONTENTFUL_ACCESS_TOKEN}`,
    locale: locale ?? 'en',
  })
}

export const getContentfulClient = (options?: ContentfulClientOptions): ApolloClient => {
  const { preview, locale } = options ?? {}

  return createContentfulClient({
    clientName: 'contentful',
    baseUrl:
      (preview ? process.env.NEXT_PUBLIC_CONTENTFUL_PROXY_PREVIEW_URL : process.env.NEXT_PUBLIC_CONTENTFUL_PROXY_URL) ??
      'https://api.angelstudios.com/cms/web',
    locale: locale ?? 'en',
  })
}

/* Can be used to test backend changes with the UI */

export const getLocalHostClient = (config?: WebClientConfig) => {
  if (isProductionEnvironment()) return

  return getMemoizedWebClient({
    clientName: 'memoized-local',
    baseUrl: config?.baseUrl ?? (process.env.NEXT_PUBLIC_LOCAL_GRAPHQL as string),
    locale: config?.locale,
    name: FEDERATION_CLIENT_NAME,
    version: FEDERATION_CLIENT_VERSION,
  })
}

const mergeContent = (existing: Record<string, unknown> = {}, incoming: Record<string, unknown> = {}) => {
  if (isEqual(existing, incoming)) {
    return existing
  }
  // Merge both existing and incoming, with incoming taking precedence
  // This ensures fields from existing are preserved if not in incoming
  return { ...existing, ...incoming }
}
